import { ViewChild, Component, Input, Inject, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { MatChipGrid, MatChipInputEvent } from '@angular/material/chips';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormControl } from '@angular/forms';
import { debounceTime, startWith } from 'rxjs/operators';

import { isUndefined, isArray, isObject, merge, cloneDeep } from 'lodash';

@Component({
    selector: 'sfx-filters-operator',
    templateUrl: './filters-operator.component.html',
    styleUrls: ['./filters-operator.component.scss']
})
export class FiltersOperatorComponent implements OnInit, OnChanges {

    @ViewChild('chipList') chipList!: MatChipGrid;
    searchedQuery = new FormControl('');

    @Input() property: any;
    @Input() fixedRule?: boolean;
    @Input() filterFields: any;
    @Input() isDisabled: boolean;
    @Input() noEditAllowed: boolean;
    @Input() propertyChanged!: (event: any) => void;
    @Input() apply!: () => void;

    operator: any;
    value: any;
    value2: any;
    valueDate: any;
    valueChip: any[] = [];
    selectedValue: any;
    selectOptions: any[] = [];
    propertyMatches: any[] = [];
    filterValueChanged = false;
    filteredOptions: any[] = [];

    textOperators: any[] = [];
    largeTextOperators: any[] = [];
    urlOperators: any[] = [];
    integerOperators: any[] = [];
    selectOperators: any[] = [];
    autoCompleteOperators: any[] = [];
    multiSelectOperators: any[] = [];
    tagOperators: any[] = [];
    dateOperators: any = { relative: [], absolute: [] };
    booleanOperators: any[] = [];
    binaryBooleanOperators: any[] = [];

    private thread: any;
    private valueObject: any = {};

    constructor(
        @Inject('utilsService') public utils: any,
        @Inject('accountsService') public accounts: any,
        @Inject('contactsService') public contactsService: any,
        @Inject('tagsService') public tagsService: any,
        @Inject('filterService') public filterService: any,
        @Inject('customFieldsService') public customfields: any,
        @Inject('usersService') public users: any,
        @Inject('pipelinesService') public pipelines: any,
        @Inject('leadsService') private leadsService: any,
        @Inject('workflowsService') public workflowsService: any
    ) {}

    ngOnInit(): void {
        this.initializeOperators();
        if (this.property && this.property.operator && this.property.type) {
            this.transformOperatorsAndValues();
        }
        else {
            this.operator = '';
            this.value = '';
            this.valueDate = '';
            this.valueChip = [];
        }

        this.setupSearchListener();
    }

    setupSearchListener = () => {
        this.searchedQuery.valueChanges.pipe(
            startWith(''),
            debounceTime(150) // Matches md-delay="150"
        ).subscribe((query) => {
            this.searchPropertyValue(query ?? undefined).then((response: any) => {
                this.filteredOptions = response;

                // Only for job titles filter on lead entity -> add the option in the list if no match is found
                if (this.property.entity === 'lead' && this.property.id === 'lead_people.job_title') {

                    if (query && query !== '' && !this.filteredOptions.some((option) => option.name.toLowerCase() === query.toLowerCase())) {
                        this.filteredOptions.unshift({
                            name: query,
                            createNew: true
                        });
                    }
                }
            });
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.filterFields && this.property?.options) {
            const filterFields = changes.filterFields.currentValue;
            let currentField;

            if (this.property.archived) {
                currentField = this.property;
            }
            else {
                currentField = filterFields.find((filterField: any) => {
                    // eslint-disable-next-line angular/definedundefined
                    if (this.property.pipeline !== undefined) {
                        if (filterField.pipeline !== this.property.pipeline) {
                            return false;
                        }
                    }

                    return filterField.id === this.property.id || filterField.query_builder_id === this.property.id;
                });
            }

            if (this.property.raw_value?.some((option: any) => option.archived === true)) {
                currentField.options = this.property.options;
                currentField.raw_value = this.property.raw_value;
            }

            if (currentField?.options) {
                if (!this.property.value) {
                    this.property.options = currentField.options;
                    this.selectOptions = this.property.options;
                }
                // eslint-disable-next-line angular/definedundefined
                else if (this.property !== undefined && currentField !== undefined
                    && !this.areArrayValuesEqual(this.property.options, currentField.options, this.areOptionObjectsEqual)) {

                    // If options are not the same correct them to the currentField options
                    this.property.options = currentField.options;
                    this.selectOptions = this.property.options;

                    // If we actually changed options (not just initial state set), remove current selected value
                    if (changes.filterFields.previousValue && Object.keys(changes.filterFields.previousValue).length > 0) {
                        this.valueObject = {};
                        this.property.value = [];
                        delete this.property.raw_value;
                        this.selectedValue = [];
                        this.propertyChanged({ operator: this.property.operator });
                    }
                }
            }
        }

        if (this.property?.options) {
            this.selectOptions = this.property.options;
        }

        if (this.property) {
            this.setupProperty();
        }
    }

    initializeOperators(): void {
        this.textOperators = this.filterService.operators.text;
        this.largeTextOperators = this.filterService.operators.text;
        this.urlOperators = this.filterService.operators.text;
        this.integerOperators = this.filterService.operators.integer;
        this.selectOperators = this.filterService.operators.select;
        this.autoCompleteOperators = this.filterService.operators.select;
        this.multiSelectOperators = this.filterService.operators.select;
        this.tagOperators = this.filterService.operators.select;
        this.dateOperators.relative = this.filterService.operators.date.filter((d: any) => d.key.startsWith('past') || d.key.startsWith('future'));
        this.dateOperators.absolute = this.filterService.operators.date.filter((d: any) => !d.key.startsWith('past') && !d.key.startsWith('future'));
        this.booleanOperators = this.filterService.operators.boolean;
        this.binaryBooleanOperators = this.filterService.operators.boolean;
    }

    getOperators(property: any): any[] {
        let operators;

        switch (property.input) {
            case 'text':
                operators = this.textOperators;
                break;
            case 'largetext':
                operators = this.largeTextOperators;
                break;
            case 'url':
                operators = this.urlOperators;
                break;
            case 'integer':
            case 'decimal':
                operators = this.integerOperators;
                break;
            case 'single select':
                operators = this.selectOperators;
                break;
            case 'multiselect':
                operators = this.multiSelectOperators;
                break;
            case 'autocomplete':
                operators = this.autoCompleteOperators;
                break;
            case 'tags':
                operators = this.tagOperators;
                break;
            case 'date':
                operators = this.dateOperators;
                break;
            case 'radio':
                operators = this.booleanOperators;
                break;
            case 'binaryradio':
                operators = this.binaryBooleanOperators;
                break;
            default:
                operators = [];
        }

        if (property?.operators?.length) {
            return operators.filter((operator: any) => property.operators.includes(operator.key));
        }

        return operators;
    }

    setupProperty(): void {
        if (this.property.operator) {
            this.transformOperatorsAndValues();

            if (this.property.operator.key) {
                this.property.operator = this.property.operator.key;
            }

            this.operator = this.property.operator;
            this.value = this.property.value;
            this.valueObject = {
                value: this.property.value,
                raw_value: this.property.raw_value
            };

            if (
                this.property.input !== 'multiselect' &&
                this.property.input !== 'tags' &&
                isArray(this.property.value) &&
                this.property.value.length === 1
            ) {
                this.property.value = this.property.value[0];
                if (!isObject(this.property.value)) {
                    this.value = this.property.value;
                }
            }

            if (isObject(this.property.value) && !isArray(this.property.value)) {
                this.value = this.property.value.value;
                this.valueObject = {
                    value: this.property.value.value,
                    raw_value: this.property.value.raw_value
                };
                delete this.property.value;
                merge(this.property, this.valueObject);
            }

            if (
                (this.property.operator === 'between' || this.property.operator === 'not_between') &&
                isArray(this.property.value) && this.property.value.length === 2
            ) {
                this.value = this.property.value[0];
                this.value2 = this.property.value[1];
            }

            if ((this.property.input === 'single select' || this.property.input === 'multiselect') && !this.selectedValue) {
                this.selectedValue = [];
            }

            switch (this.property.input) {
                case 'single select':
                    if (this.selectedValue.length !== this.valueObject.value.length) {
                        this.selectedValue = this.valueObject.value;
                    }

                    break;
                case 'multiselect':
                    if (
                        this.selectedValue &&
                        this.value &&
                        (this.selectedValue.length !== this.value.length || (!this.selectedValue && this.value))
                    ) {
                        this.selectedValue = this.value;
                    }

                    break;
                case 'autocomplete':
                case 'tags':
                    this.valueChip = this.valueObject.raw_value ? this.valueObject.raw_value : [];
                    break;
                case 'date':
                    if (this.operator === 'after' || this.operator === 'before' || this.operator === 'on') {
                        this.valueDate = this.utils.UTCDateStringToLocalDateObject(this.valueObject.value);
                    }

                    break;
                default:
                    break;
            }
        }
        else {
            return this.setDefaultOperator();
        }
    }

    setDefaultOperator(): void {
        if (this.property?.operators) {
            this.operator = this.property.operators[0];
            return this.changeProperty();
        }

        switch (this.property.input) {
            case 'text':
            case 'largetext':
            case 'url':
                this.operator = 'contains';

                return this.changeProperty();
            case 'integer':
            case 'decimal':
                this.operator = 'equal';

                return this.changeProperty();
            case 'single select':
            case 'multiselect':
            case 'autocomplete':
            case 'tags':
                this.operator = 'in';

                return this.changeProperty();
            case 'date':
                this.operator = 'past_exactly';

                return this.changeDateProperty();
            case 'radio':
            case 'binaryradio':
                this.operator = 'true';

                return this.changeProperty();
        }
    }

    changeProperty(): void {
        if (
            this.operator === 'is_empty' || this.operator === 'is_not_empty' ||
            this.operator === 'is_null' || this.operator === 'is_not_null' ||
            this.operator === 'true' || this.operator === 'false'
        ) {
            this.propertyChanged({ operator: this.operator, value: '' });
            this.filterValueChanged = false;

            return this.apply();
        }

        // If the operator normally accepts a single value, but now holds an array
        // reset the value, to avoid errors
        // operators that do accept arrays should be added to the if statement
        if (
            this.operator !== 'in' && this.operator !== 'not_in' &&
            this.operator !== 'between' && this.operator !== 'not_between' &&
            isArray(this.valueObject.value)
        ) {
            this.valueObject.value = this.value;
            delete this.value2;
            this.changePropertyValue();
        }

        const newProperty = cloneDeep(this.valueObject);
        newProperty.operator = this.operator;
        this.propertyChanged(newProperty);

        // eslint-disable-next-line angular/definedundefined
        const hasValue = Object.values(this.valueObject).some((val) => val !== '' && val !== undefined);
        if (hasValue) {
            this.filterValueChanged = false;
            return this.apply();
        }
    }

    changePropertyValue(): void {
        if (!this.filterValueChanged) {
            this.filterValueChanged = true;
        }

        switch (this.property.input) {
            case 'integer':
            case 'decimal':
                if (this.operator === 'between' || this.operator === 'not_between') {
                    this.valueObject.value = [this.value, this.value2];
                }
                else {
                    this.valueObject.value = this.value;
                }

                break;

            case 'single select': {
                this.valueObject.value = this.selectedValue.id;

                const newPropertySingle = cloneDeep(this.valueObject);
                newPropertySingle.operator = this.operator;

                this.propertyChanged(newPropertySingle);
                this.filterValueChanged = false;

                return this.apply();
            }

            case 'multiselect':
                if (!this.areArrayValuesEqual(this.valueObject.value, this.selectedValue)) {
                    this.valueObject.value = this.selectedValue;
                    const newPropertyMulti = cloneDeep(this.valueObject);
                    newPropertyMulti.operator = this.operator;

                    this.propertyChanged(newPropertyMulti);
                    this.filterValueChanged = false;

                    return this.apply();
                }

                return;

            case 'autocomplete':
            case 'tags': {
                this.valueObject.value = this.valueChip.map((chip) => chip.id ?? chip.name);
                this.valueObject.raw_value = this.valueChip;

                const newProperty = cloneDeep(this.valueObject);
                newProperty.operator = this.operator;

                this.propertyChanged(newProperty);
                this.filterValueChanged = false;

                return this.apply();
            }

            case 'date':
                if (this.operator === 'after' || this.operator === 'before' || this.operator === 'on') {
                    this.valueObject.value = this.valueDate;
                    if (this.value) {
                        this.value = '';
                    }
                }
                else {
                    this.valueObject.value = this.value;
                    if (this.valueDate) {
                        this.valueDate = '';
                    }
                }

                break;

            default:
                this.valueObject.value = this.value;
        }

        const newProperty = cloneDeep(this.valueObject);
        newProperty.operator = this.operator;
        this.propertyChanged(newProperty);

        if (this.thread) {
            clearTimeout(this.thread);
        }

        // eslint-disable-next-line angular/timeout-service
        this.thread = setTimeout(() => {
            this.applyWrapper();
        }, 750);
    }

    changeDateProperty(): void {
        if (this.value && !this.valueDate) {
            this.value = '';
        }

        this.propertyChanged({
            operator: this.operator,
            value: { value: this.valueDate }
        });
    }

    showInputField(): boolean {
        return this.operator && !['is_empty', 'is_not_empty', 'is_null', 'is_not_null'].includes(this.operator);
    }

    applyWrapper(): void {
        if (this.filterValueChanged) {
            this.filterValueChanged = false;
            return this.apply();
        }
    }

    addChip(event: MatChipInputEvent): void {
        const input = event.input;
        const value = event.value;

        if ((value || '').trim()) {
            this.valueChip.push({ name: value.trim() });
            this.changePropertyValue();
        }

        if (input) {
            input.value = '';
        }

        this.searchedQuery.setValue('');
    }

    displayFn(value: any): string {
        return value?.name || '';
    }

    removeChip(chip: any): void {
        const index = this.valueChip.indexOf(chip);

        if (index >= 0) {
            this.valueChip.splice(index, 1);
            this.changePropertyValue();
        }
    }

    selectAutocompleteOption(event: MatAutocompleteSelectedEvent): void {
        this.valueChip.push(event.option.value);
        this.changePropertyValue();
        this.searchedQuery.setValue('');
    }

    areArrayValuesEqual(originalArray: any[], newArray: any[], compareFn?: (a: any, b: any) => boolean): boolean {
        if (!originalArray && !newArray) {
            return true;
        }

        // eslint-disable-next-line angular/definedundefined
        if ((!originalArray && newArray !== undefined) || (!newArray && originalArray !== undefined)) {
            return false;
        }

        if (originalArray.length !== newArray.length) {
            return false;
        }

        for (const [i, element] of originalArray.entries()) {
            // eslint-disable-next-line angular/definedundefined
            if (compareFn !== undefined && !compareFn(element, newArray[i])) {
                return false;
            }
            else if (isUndefined(compareFn) && element !== newArray[i]) {
                return false;
            }
        }

        return true;
    }

    areOptionObjectsEqual(a: any, b: any): boolean {
        return a.id === b.id && a.name === b.name && a.order === b.order && a.archived === b.archived;
    }

    // Searches for values available for auto-complete inputs
    searchPropertyValue(queryString?: string): any {

        if (!queryString || queryString === '') {
            queryString = undefined;
        }

        // We use the query builder id if it is there so older clients can talk to a newer server
        const forwardCompatibleId = this.property.query_builder_id || this.property.id;

        if (queryString && forwardCompatibleId.includes('custom')) {
            if (this.property.search_entity) {
                switch (this.property.search_entity) {
                    case 'account':
                        return this.accounts.get(queryString).then(this.searchResponse, null, this.searchResponse);
                    case 'contact':
                        return this.contactsService.get(queryString).then(this.searchResponse, null, this.searchResponse);
                    case 'user':
                        return this.users.get(queryString, false, ['user.disabled', 'name']).then(this.searchResponse, null, this.searchResponse);
                }
            }

            const customFieldName = this.property.name ? this.property.name : this.property.label;
            return this.customfields.getOptions(customFieldName, this.property.entity_ui.toLowerCase(), queryString).then(this.searchResponse, null, this.searchResponse);
        }

        switch (forwardCompatibleId) {
            case 'account.owner':
            case 'account-user.user':
            case 'campaign.creator':
            case 'opportunity.assignee':
            case 'opportunity.creator':
            case 'opportunity.owner':
            case 'task.creator':
            case 'task.completor':
            case 'task_assignee.assignee':
            case 'account.owner.id': //New id's
            case 'account.team_member.id':
            case 'campaign.created_by.id':
            case 'opportunity.assignee.id':
            case 'opportunity.created_by.id':
            case 'opportunity.owner.id':
            case 'task.created_by.id':
            case 'task.completed_by':
            case 'task.assignee.id':
            case 'workflow.created_by.id':
            case 'contact.strongest_relationship':
            case 'account.strongest_relationship':
                return this.users.get(queryString, false, ['user.disabled', 'name']).then(this.searchResponse, null, this.searchResponse);
            case 'account-contact.account':
            case 'opportunity.account':
            case 'task.account':
            case 'contact.account.id'://New id's
            case 'opportunity.account.id':
            case 'task.account.id':
                return this.accounts.get(queryString).then(this.searchResponse, null, this.searchResponse);
            case 'account-contact.contact':
            case 'campaign-opened.contact':
            case 'campaign-clicked.contact':
            case 'campaign-replied.contact':
            case 'campaign_email.contact':
            case 'account.contact.id'://New id's
            case 'campaign.opened_by.id':
            case 'campaign.clicked_by.id':
            case 'campaign.replied_by.id':
            case 'campaign.contact.id':
            case 'workflow.entered.id':
            case 'workflow.in.id':
            case 'workflow.exited.id':
            case 'workflow.met_goal.id':
            case 'workflow.contact.id':
                return this.contactsService.get(queryString).then(this.searchResponse, null, this.searchResponse);
            case 'account-tag.id':
            case 'opportunity-tag.id':
            case 'person-tag.id':
            case 'account.tag.id'://New id's
            case 'opportunity.tag.id':
            case 'contact.tag.id':
                return this.tagsService.get({ search: queryString, limit: 50 }).then(this.searchResponse, null, this.searchResponse);
            case 'pipeline.id':
            case 'opportunity.pipeline.id': //New id
                return this.pipelines.get(queryString).then(this.searchResponse, null, this.searchResponse);
            case 'workflow_record.send_email.received':
            case 'workflow_record.send_email.not_received':
            case 'workflow_record.send_email.opened':
            case 'workflow_record.send_email.not_opened':
            case 'workflow_record.send_email.clicked':
            case 'workflow_record.send_email.not_clicked':
            case 'workflow_record.send_email.replied':
            case 'workflow_record.send_email.not_replied':
                return this.workflowsService.get({ search: queryString }, {}).then(this.searchResponse, null, this.searchResponse);
            case 'lead_company.industry':
                return queryString ? this.leadsService.autocompleteLookup(queryString ?? '', 'industries').then((response: any) => this.searchResponse(response)).catch(this.searchResponse) : Promise.resolve([]);
            case 'lead_people.job_title':
                return queryString ? this.leadsService.autocompleteLookup(queryString ?? '', 'job_titles').then((response: any) => this.searchResponse(response)).catch(this.searchResponse) : Promise.resolve([]);
            case 'lead_people.location':
                return queryString ? this.leadsService.autocompleteLookup(queryString ?? '', 'locations').then((response: any) => this.searchResponse(response)).catch(this.searchResponse) : Promise.resolve([]);
            default:
                return Promise.resolve([]);
        }
    }

    searchResponse(response: any) {

        if (!response || !response.data || response.data.error) {
            return Promise.resolve([]);
        }

        const responseData = response.data.filter((result: any) => {

            let foundDuplicate = false;
            this.valueChip.forEach((value: any) => {

                if (value.name === result.name) {
                    foundDuplicate = true;
                }

            });
            return !foundDuplicate;
        });

        return Promise.resolve(responseData);
    }

    /**
     * This can take API valid rules and translates them to client rules
     */
    transformOperatorsAndValues(): any {

        if (isUndefined(this.property.operator) || isUndefined(this.property.value)) {
            return;
        }

        // eslint-disable-next-line angular/definedundefined
        const operator = this.property.operator.key === undefined ? this.property.operator : this.property.operator.key;
        // eslint-disable-next-line angular/definedundefined
        let value = this.property.value.value === undefined ? this.property.value : this.property.value.value;

        if (isArray(value)) {
            value = value[0];
        }

        if (operator === 'equal' || operator === 'is') {
            switch (value) {
                case '':
                    this.property.operator = 'is_empty';
                    this.property.value = '';
                    break;

                case 'null':
                    if (this.property.input === 'text' || this.property.input === 'largetext' || this.property.input === 'integer' || this.property.input === 'decimal' || this.property.input === 'url') {
                        this.property.operator = 'is_null';
                        this.property.value = '';
                    }
                    else if (this.property.input === 'date') {
                        this.property.operator = 'is_null';
                        this.property.value = '';
                    }
                    else if (this.property.input === 'binaryradio' && operator === 'equal') {
                        this.property.operator = 'false';
                        this.property.value = '';
                    }
                    else {
                        this.property.operator = 'is_null';
                        this.property.value = '';
                    }

                    break;

                case 'true':
                    this.property.operator = 'true';
                    this.property.value = '';
                    break;

                case 'false':
                    this.property.operator = 'false';
                    this.property.value = '';
                    break;

                default:
                    break;
            }
        }

        if (operator === 'not_equal' || operator === 'is_not') {
            switch (value) {
                case '':
                    this.property.operator = 'is_not_empty';
                    this.property.value = '';
                    break;

                case 'null':
                    if (this.property.input === 'text' || this.property.input === 'largetext' || this.property.input === 'integer' || this.property.input === 'decimal' || this.property.input === 'url') {
                        this.property.operator = 'is_not_null';
                        this.property.value = '';
                    }
                    else {
                        this.property.operator = 'is_not_null';
                        this.property.value = '';
                    }

                    break;

                default:
                    break;
            }
        }
    }
}
